import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Subject, take, takeUntil } from 'rxjs';
import { GoalsModel, GoalTasksModel, RefreshEvents, UpdateTaskModel } from 'src/app/models/goals.model';
import { GoalService } from 'src/app/services/goal.service';
import { MatDialog } from '@angular/material/dialog';
import { GoalsUpdateModalComponent } from '../goals-modals/goals-update-modal/goals-update-modal.component';
import { DatePipe } from '@angular/common';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-goals-item',
  templateUrl: './goals-item.component.html',
  styleUrls: ['./goals-item.component.scss']
})
export class GoalsItemComponent implements OnInit {

  @Input() goalsData: GoalsModel;
  @Output() refreshDataEvent = new EventEmitter();

  goalProgress = 0;
  overdueTasks = 0;
  isGoalOverDue = false;
  currentDate = new Date();
  refreshEvent = RefreshEvents;

  private destroyed$ = new Subject();

  constructor(
    private _goalService: GoalService,
    private _dialog: MatDialog,
    private _datePipe: DatePipe,
    private _snackbar: MatSnackBar,
		private _translator: TranslateService,
  ) { }

  ngOnInit(): void {
    this.getGoalStatus();
  }

  getGoalStatus() {
    this.goalProgress = this.getGoalProgress(this.goalsData);
    this.getOverdueTasks(this.goalsData);
  }

  getGoalProgress(goalsData: GoalsModel) {
    let totalTasks = 0;
    let completedTasks = 0;

    if(goalsData.goalTasks.length == 0) return 0;

    totalTasks = goalsData.goalTasks.length;
    completedTasks += goalsData.goalTasks.filter(task => task.isDone).length

    const percentage = (completedTasks / totalTasks) * 100;
    return Math.round(percentage * 100) / 100;
  }

  getOverdueTasks(goalsData: GoalsModel) {
    this.overdueTasks = 0;

    goalsData.goalTasks.forEach(task => {
      const dueDate = new Date(task.due);
      if(!task.isDone && dueDate < this.currentDate) {
          this.overdueTasks++;
      }
    });
    return this.overdueTasks;
  }

  onChecked(goalTaskId: number, isDone: boolean): void {
    const goalTask = this.goalsData.goalTasks.find(task => task.goalTaskId === goalTaskId);
    if(goalTask) {
      goalTask.isDone = isDone;

      const updateTask: UpdateTaskModel = {
        goalTaskId: goalTask.goalTaskId,
        isDone: goalTask.isDone
      };

      this._goalService.updateTaskCompletion(updateTask)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response) => {
          this._snackbar.open(
            this._translator.instant(
              'Task updated successfully.'
            ),
            this._translator.instant('Dismiss'),
            {
              duration: 3000,
            }
          );
          this.getGoalStatus();
          this.refreshData(this.refreshEvent.REFRESH_RISK_COUNTER);
        },
        error: (error) => {
          this._snackbar.open(
            this._translator.instant(
              'There was an error updating the task.'
            ),
            this._translator.instant('Dismiss'),
            {
              duration: 3000,
            }
          );
          console.error(`Error updating task:`, error);
        }
      })
    }
  }

  onMoreButtonClick(event: Event)
  {
    event.stopPropagation();
  }

  onEditGoal() {
    let ref = this._dialog.open(GoalsUpdateModalComponent, {
      width: '700px',
      maxHeight: '100vh',
      panelClass: 'create-dialog',
      data: this.goalsData
    });

    ref.afterClosed().subscribe(val => {
      if(val) {
        let formattedDate = this._datePipe.transform(val.formValues.goal.due, 'shortDate')
        const goalModel: GoalsModel = {
          studentGoalId: this.goalsData.studentGoalId,
          name: val.formValues.goal.name,
          due: formattedDate,
          description: val.formValues.goal.description,
          goalTasks: val.formValues.tasks
        };

        this._goalService.updateStudentGoal(this.goalsData.studentGoalId, goalModel).pipe(
          takeUntil(this.destroyed$))
          .subscribe({
            next: (data) => {
              this._snackbar.open(
                this._translator.instant(
                  'The goal has been successfully updated.'
                ),
                this._translator.instant('Dismiss'),
                {
                  duration: 3000,
                }
              );
              this.refreshData(this.refreshEvent.REFRESH_ALL_GOALS);
              console.log("Goal successfully updated.");
            },
            error: (error) => {
              this._snackbar.open(
                this._translator.instant(
                  'Failed to update your goal. Please try again.'
                ),
                this._translator.instant('Dismiss'),
                {
                  duration: 3000,
                }
              );
              console.log("There was an error updating the goal.", error);
            }
          })
      }
      
    });
  }

  refreshData(action: string) {
    this.refreshDataEvent.emit(action);
  }

  ngOnDestroy() 
  {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  getDateFromEpoch(epochDate: number) {
    var date = new Date(epochDate * 1000);
    return date.toUTCString();
  }

}
